/* eslint-disable */

import React, { useMemo, useState as useStateHook } from 'react'
import { api } from '../requests/axios'
import { Box, Grid, Card, CardContent, Typography, Pagination, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useEffect, useState } from 'react'
import ColorChart from 'components/ColorChart'
import { useNavigate } from "react-router-dom"
import CraftSelectionModal from '../components/CraftSelectionModal'
import CrochetStitchModal from '../components/CrochetStitchModal'
import { getCellDimensions } from '../utils/cellDimensions'
import { getCachedData, setCachedData, clearCache } from '../utils/apiCache'

interface ColorChart {
  id: number;
  name: string;
  description: string | null;
  data: string;
  height: number;
  width: number;
  craft?: string;
  crochet_stitch_type?: string;
}

interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface ColorChartsResponse {
  color_charts: ColorChart[];
  pagination: PaginationMeta;
}

// Memoized chart card component to prevent unnecessary re-renders
const ChartCard = React.memo(({ 
  color_chart, 
  navigate, 
  onDelete 
}: { 
  color_chart: ColorChart; 
  navigate: (path: string) => void;
  onDelete: (id: number) => void;
}) => {
  const [isHovered, setIsHovered] = useStateHook(false);
  // Get cell dimensions based on craft and stitch type
  const cellDimensions = useMemo(() => {
    const craft = color_chart.craft as 'knitting' | 'crochet' | 'cross_stitch' | null | undefined
    const crochetStitchType = color_chart.crochet_stitch_type as 'sc' | 'hdc' | 'dc' | null | undefined
    return getCellDimensions(craft || null, crochetStitchType || null)
  }, [color_chart.craft, color_chart.crochet_stitch_type])

  // Calculate scale to fit chart in preview area (max 250px)
  const boxWidth = cellDimensions.width + cellDimensions.margin * 2;
  const boxHeight = cellDimensions.height + cellDimensions.margin * 2;
  const chartWidth = color_chart.width * boxWidth;
  const chartHeight = color_chart.height * boxHeight;
  const maxDimension = 250;
  const scale = Math.min(1, maxDimension / Math.max(chartWidth, chartHeight));

  // Get craft label
  const getCraftLabel = () => {
    if (!color_chart.craft) return null;
    
    const craftLabels: Record<string, string> = {
      knitting: 'Knitting',
      crochet: 'Crochet',
      cross_stitch: 'Cross Stitch'
    }
    
    const stitchLabels: Record<string, string> = {
      sc: 'Single Crochet',
      hdc: 'Half Double Crochet',
      dc: 'Double Crochet'
    }
    
    if (color_chart.craft === 'crochet' && color_chart.crochet_stitch_type) {
      return `${stitchLabels[color_chart.crochet_stitch_type]}`
    }
    
    return craftLabels[color_chart.craft] || null;
  }

  const craftLabel = getCraftLabel();

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    onDelete(color_chart.id);
  };

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card 
        onClick={() => navigate(`/color-charts/${color_chart.id}`)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        {/* Delete button - visible on hover */}
        <IconButton
          onClick={handleDeleteClick}
          aria-label="delete chart"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              color: 'error.main'
            }
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>

        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              mb: 2, 
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: 4 // Add padding to prevent overlap with delete button
            }}
          >
            {color_chart.name}
          </Typography>
          
          {color_chart.description && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ mb: 2, minHeight: 40 }}
            >
              {color_chart.description}
            </Typography>
          )}

          <Box 
            sx={{ 
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              padding: 2,
              minHeight: 150,
              maxHeight: 300,
              position: 'relative'
            }}
          >
            <Box
              sx={{
                transform: `scale(${scale})`,
                transformOrigin: 'center',
                width: chartWidth,
                height: chartHeight
              }}
            >
              <ColorChart color_chart={color_chart} />
            </Box>
          </Box>

          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ mt: 2, textAlign: 'center' }}
          >
            {color_chart.width} × {color_chart.height} stitches
            {craftLabel && ` • ${craftLabel}`}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );
});

ChartCard.displayName = 'ChartCard';

export default function ColorCharts() {
  const [colorChartsData, setColorChartsData] = useState<ColorChart[]>([])
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [showCraftModal, setShowCraftModal] = useState(false)
  const [showStitchModal, setShowStitchModal] = useState(false)
  const [selectedCraft, setSelectedCraft] = useState<'knitting' | 'crochet' | 'cross_stitch' | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [chartToDelete, setChartToDelete] = useState<ColorChart | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' })
  const navigate = useNavigate()

  const setData = async (pageNum: number = 1) => {
    // Check cache first (cache color charts for 5 minutes)
    const cacheKey = `color_charts_page_${pageNum}`;
    const cached = getCachedData<ColorChartsResponse>(cacheKey);
    if (cached) {
      setColorChartsData(Array.isArray(cached.color_charts) ? cached.color_charts : [])
      setPagination(cached.pagination)
      // Still fetch in background to update cache
    }

    try {
      const response = await api.get(
        (process.env.REACT_APP_API_PROXY_URL || '').concat(`/color_charts.json?page=${pageNum}`)
      )
      const data: ColorChartsResponse = await response.data
      setColorChartsData(Array.isArray(data.color_charts) ? data.color_charts : [])
      setPagination(data.pagination)
      // Cache for 5 minutes
      setCachedData(cacheKey, data, 5 * 60 * 1000)
    } catch (error) {
      console.error('Error fetching color charts:', error)
      // If fetch fails and we have cached data, keep using it
      const cachedData = cached || getCachedData<ColorChartsResponse>(cacheKey);
      if (cachedData) {
        setColorChartsData(Array.isArray(cachedData.color_charts) ? cachedData.color_charts : [])
        setPagination(cachedData.pagination)
      }
    }
  }

  useEffect(() => {
    setData(page)
  }, [page])

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNewChartClick = () => {
    setShowCraftModal(true)
  }

  const handleCraftSelect = (craft: 'knitting' | 'crochet' | 'cross_stitch') => {
    setSelectedCraft(craft)
    setShowCraftModal(false)
    
    if (craft === 'crochet') {
      setShowStitchModal(true)
    } else {
      navigate(`/color-charts/new?craft=${craft}`)
    }
  }

  const handleStitchSelect = (stitchType: 'sc' | 'hdc' | 'dc') => {
    setShowStitchModal(false)
    navigate(`/color-charts/new?craft=${selectedCraft}&crochet_stitch_type=${stitchType}`)
  }

  const handleDeleteClick = (id: number) => {
    const chart = colorChartsData.find(c => c.id === id)
    if (chart) {
      setChartToDelete(chart)
      setDeleteDialogOpen(true)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!chartToDelete) return

    setIsDeleting(true)
    const chartId = chartToDelete.id
    const chartName = chartToDelete.name
    
    // Optimistic update - remove from list immediately
    const originalCharts = [...colorChartsData]
    setColorChartsData(prev => prev.filter(c => c.id !== chartId))
    
    // Clear cache for this page
    const cacheKey = `color_charts_page_${page}`;
    clearCache(cacheKey)

    try {
      await api.delete(`${process.env.REACT_APP_API_PROXY_URL || ''}/color_charts/${chartId}.json`)
      
      setSnackbar({ open: true, message: `"${chartName}" deleted successfully`, severity: 'success' })
      setDeleteDialogOpen(false)
      setChartToDelete(null)
      
      // Refresh data to ensure consistency
      await setData(page)
    } catch (error) {
      console.error('Error deleting chart:', error)
      // Rollback on error
      setColorChartsData(originalCharts)
      setSnackbar({ open: true, message: 'Failed to delete chart. Please try again.', severity: 'error' })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setChartToDelete(null)
  }

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Button 
        variant="contained" 
        onClick={handleNewChartClick}
        sx={{ mb: 2, float: 'right' }}
      >
        New Color Chart
      </Button>
      
      {showCraftModal && (
        <CraftSelectionModal
          onClose={() => setShowCraftModal(false)}
          onSelect={handleCraftSelect}
        />
      )}
      
      {showStitchModal && (
        <CrochetStitchModal
          onClose={() => {
            setShowStitchModal(false)
            setSelectedCraft(null)
          }}
          onSelect={handleStitchSelect}
        />
      )}
      {colorChartsData.length === 0 && (
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>Create a color chart to get started!</Typography>
      )}

      <Grid container spacing={3}>
        {(colorChartsData || []).map((color_chart) => (
          <ChartCard 
            key={color_chart.id} 
            color_chart={color_chart} 
            navigate={navigate}
            onDelete={handleDeleteClick}
          />
        ))}
      </Grid>
      {pagination && pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.current_page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Color Chart?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete <strong>"{chartToDelete?.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
